class ProductRecommendations extends HTMLElement {
  /** @type {number} */
  #retryCount = 0;
  /** @type {boolean} */
  #isLoading = false;

  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    const watchedAttributes = new Set([
      'data-product-id',
      'data-section-id',
      'data-intent',
      'data-url',
      'data-recommendations-performed',
    ]);

    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;
      if (!mutation.attributeName || !watchedAttributes.has(mutation.attributeName)) continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      // All other attribute changes trigger a reload
      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  connectedCallback() {
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
    this.#syncComplementaryVisibility();
    this.#setupComplementaryPagination();

    // Fallback load in case intersection doesn't trigger reliably.
    setTimeout(() => {
      if (this.dataset.recommendationsPerformed !== 'true' && !this.#isLoading) {
        this.#loadRecommendations();
      }
    }, 1000);
  }

  /**
   * Load the product recommendations
   */
  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;
    const id = this.id;

    if (!productId || !id) {
      return;
    }

    // If the recommendations have already been loaded, accounts for the case where the Theme Editor
    // is loaded the section from the editor's visual preview context.
    if (recommendationsPerformed === 'true') {
      return;
    }

    if (this.#isLoading) return;
    this.#isLoading = true;

    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then((result) => {
        if (!result.success) {
          // The Theme Editor will place a section element element in the DOM whose section_id is not available
          // to the Section Renderer API. In this case, we can safely ignore the error.
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          this.#retryComplementaryLoad();
          return;
        }

        const html = document.createElement('div');
        html.innerHTML = result.data || '';
        const recommendations = html.querySelector(`product-recommendations[id="${id}"]`);

        if (recommendations?.innerHTML && recommendations.innerHTML.trim().length) {
          this.dataset.recommendationsPerformed = 'true';
          this.#retryCount = 0;
          if (recommendations.dataset.hasRecommendations) {
            this.dataset.hasRecommendations = recommendations.dataset.hasRecommendations;
          }
          this.innerHTML = recommendations.innerHTML;
          this.#syncComplementaryVisibility();
          this.#setupComplementaryPagination();
        } else {
          this.#handleError(new Error('No recommendations available'));
          this.#retryComplementaryLoad();
        }
      })
      .catch((e) => {
        this.#handleError(e);
        this.#retryComplementaryLoad();
      })
      .finally(() => {
        this.#isLoading = false;
      });
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Product recommendations error:', error);
    if (this.dataset.intent !== 'complementary') {
      this.classList.add('hidden');
    }
  }

  #retryComplementaryLoad() {
    if (this.dataset.intent !== 'complementary') return;
    if (this.dataset.recommendationsPerformed === 'true') return;
    if (this.#retryCount >= 2) return;

    this.#retryCount += 1;
    const delayMs = 350 * this.#retryCount;
    setTimeout(() => {
      if (this.dataset.recommendationsPerformed !== 'true') {
        this.#loadRecommendations();
      }
    }, delayMs);
  }

  #setupComplementaryPagination() {
    if (this.dataset.intent !== 'complementary') return;

    const items = [...this.querySelectorAll('[data-complementary-item]')];
    if (!items.length) return;

    const prevButton = this.querySelector('[data-complementary-prev]');
    const nextButton = this.querySelector('[data-complementary-next]');
    const pageLabel = this.querySelector('[data-complementary-page-label]');
    const pagination = this.querySelector('[data-complementary-pagination]');

    const perPage = 2;
    const totalPages = Math.max(1, Math.ceil(items.length / perPage));
    let currentPage = 1;

    const render = () => {
      for (const item of items) {
        const page = Number(item.getAttribute('data-page') || '1');
        item.hidden = page !== currentPage;
      }

      if (pageLabel) {
        pageLabel.textContent = `${currentPage}/${totalPages}`;
      }

      if (prevButton instanceof HTMLButtonElement) {
        prevButton.disabled = currentPage <= 1;
      }
      if (nextButton instanceof HTMLButtonElement) {
        nextButton.disabled = currentPage >= totalPages;
      }
      if (pagination) {
        pagination.toggleAttribute('hidden', totalPages <= 1);
      }
    };

    if (prevButton instanceof HTMLButtonElement && prevButton.dataset.bound !== 'true') {
      prevButton.addEventListener('click', () => {
        if (currentPage <= 1) return;
        currentPage -= 1;
        render();
      });
      prevButton.dataset.bound = 'true';
    }

    if (nextButton instanceof HTMLButtonElement && nextButton.dataset.bound !== 'true') {
      nextButton.addEventListener('click', () => {
        if (currentPage >= totalPages) return;
        currentPage += 1;
        render();
      });
      nextButton.dataset.bound = 'true';
    }

    render();
  }

  #syncComplementaryVisibility() {
    if (this.dataset.intent !== 'complementary') return;

    const recommendationsLoaded = this.dataset.recommendationsPerformed === 'true';
    const hasRecommendations = this.dataset.hasRecommendations === 'true';
    this.toggleAttribute('hidden', recommendationsLoaded && !hasRecommendations);
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
